import { REDDIT_CLICK_ID_COOKIE_DAYS, REDDIT_CLICK_ID_PARAM, readRedditIds } from '~/utils/tracking/redditClickIds';
import { safeTrackingCookies } from '~/utils/tracking/safeTrackingCookies';

// Cookie helpers
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }
  return null;
}

function setCookie(name: string, value: string, days = 90): void {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getSgtmPreviewToken(): string | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  // First check URL parameter
  const urlToken = url.searchParams.get('sgtm_preview');
  if (urlToken) return urlToken;
  // Fallback to cookie if not in URL
  return getCookie('sgtm_preview');
}

function getAllCookies(): Record<string, string> {
  if (typeof document === 'undefined') return {};
  return document.cookie
    .split('; ')
    .filter(Boolean)
    .reduce(
      (acc, kv) => {
        const idx = kv.indexOf('=');
        const k = idx > -1 ? kv.slice(0, idx) : kv;
        const v = idx > -1 ? kv.slice(idx + 1) : '';
        acc[k] = decodeURIComponent(v);
        return acc;
      },
      {} as Record<string, string>,
    );
}

// GA4 session id/number live in the `_ga_<streamId>` cookie (NOT the `_ga`
// client-id cookie). Value looks like "GS1.1.<sessionId>.<sessionNo>.…".
// We forward these so the server-side Measurement Protocol events stitch onto
// the visitor's real browser session — without `session_id` GA4 spins up a
// session-less context per event, which is why server-sent conversions
// (sign_up, masterclass_lead, …) showed Landing page = "(not set)".
function getGaSession(): { id: string | null; number: string | null } {
  if (typeof document === 'undefined') return { id: null, number: null };
  const all = getAllCookies();
  const gaKey = Object.keys(all).find((k) => /^_ga_/.test(k));
  if (!gaKey) return { id: null, number: null };
  const parts = all[gaKey].split('.');
  // GS1.1.<sessionId>.<sessionNumber>.…
  return { id: parts[2] || null, number: parts[3] || null };
}

// Matched case-insensitively (landers send ?clickid, ?clickId, ?bclickId and ?bclickid).
const BINOM_CLICK_ID_PARAMS = ['clickid', 'bclickid'];
const BINOM_CLICK_ID_COOKIE = 'cid_clickid';

// Store click ids as 1p cookies so server can use them later
function persistClickIds(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const clickIdParams = [
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'ttclid',
    'msclkid',
    'twclid',
    'sccid', // snap
    'ob_click_id', // outbrain sometimes
    'tblci', // taboola often uses tblci
    REDDIT_CLICK_ID_PARAM, // reddit (90-day cookie)
  ];

  clickIdParams.forEach((p) => {
    const val = url.searchParams.get(p);
    if (val) setCookie(`cid_${p}`, val, p === REDDIT_CLICK_ID_PARAM ? REDDIT_CLICK_ID_COOKIE_DAYS : 30);
  });

  // Binom / ThriveTrack click id — captured on ANY landing page (homepage, LPs,
  // /masterclass) so the signup conversion + Binom postbacks can use it later.
  // Ad links carry it as ?clickid (tracker default) or the legacy ?bclickId.
  const binomClickId = [...url.searchParams.entries()].find(
    ([key, value]) => BINOM_CLICK_ID_PARAMS.includes(key.toLowerCase()) && value,
  )?.[1];
  if (binomClickId) setCookie(BINOM_CLICK_ID_COOKIE, binomClickId, 30);

  // Also persist UTMs
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  utmParams.forEach((p) => {
    const val = url.searchParams.get(p);
    if (val) {
      // Remove 'utm_' prefix for cookie name to avoid duplication
      const cookieName = p.startsWith('utm_') ? p : `utm_${p}`;
      setCookie(cookieName, val, 30);
    }
  });

  // Persist Everflow parameters
  const everflowParams = [
    'uid',
    'oid',
    'affid',
    'sub1',
    'sub2',
    'sub3',
    'sub4',
    'sub5',
    'source_id',
    '_ef_transaction_id',
  ];
  everflowParams.forEach((p) => {
    const val = url.searchParams.get(p);
    if (val) setCookie(`ef_${p}`, val, 30);
  });

  // Persist sGTM preview token
  const sgtmPreview = url.searchParams.get('sgtm_preview');
  if (sgtmPreview) {
    setCookie('sgtm_preview', sgtmPreview, 30);
  }
}

// Record the public project/developer page a guest organically landed on, so a
// later signup can be attributed to it server-side (see captureAttribution.ts).
// First-touch wins — we don't overwrite — so the page that originally brought
// the visitor in is preserved even if they browse around before registering.
function recordRegistrationSource(src: { type: 'project' | 'developer'; slug: string; name: string }): void {
  if (typeof document === 'undefined') return;
  if (!src.slug || !src.name) return;
  if (getCookie('ts_reg_source')) return; // first-touch wins
  setCookie('ts_reg_source', `${src.type}|${src.slug}|${src.name}`, 30);
}

// Flatten the captured registration source (ts_reg_source cookie) into GA4
// event params, so a signup event can be attributed to the project/developer
// page that brought the lead in. Returns {} when nothing was captured.
function registrationSourceParams(): Record<string, string> {
  const raw = getCookie('ts_reg_source');
  if (!raw) return {};
  const sep = raw.indexOf('|');
  const sep2 = sep === -1 ? -1 : raw.indexOf('|', sep + 1);
  if (sep === -1 || sep2 === -1) return {};
  const type = raw.slice(0, sep).trim();
  const slug = raw.slice(sep + 1, sep2).trim();
  const name = raw.slice(sep2 + 1).trim();
  if (!type || !slug || !name) return {};
  return {
    registration_source: name,
    registration_source_type: type,
    registration_source_id: slug,
  };
}

// Ensure visitor ID exists
function ensureVisitorId(): string {
  const cookieName = 'ts_vid';
  let vid = getCookie(cookieName);
  if (!vid) {
    vid =
      (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
      `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    setCookie(cookieName, vid, 365);
  }
  return vid;
}

interface SendEventOptions {
  event_id?: string;
  cookieAllowlist?: string[];
}

export const useTracking = () => {
  const sendEventToBackend = async (
    eventName: string,
    eventData: Record<string, any> = {},
    options: SendEventOptions = {},
  ) => {
    if (typeof window === 'undefined') return;

    const visitorId = ensureVisitorId();
    const authStore = useAuthStore();

    // Optional: limit cookies you send (privacy + payload size)
    const cookieAllowlist = options.cookieAllowlist || null;
    const allCookies = getAllCookies();
    const cookies = safeTrackingCookies(
      cookieAllowlist
        ? Object.fromEntries(Object.entries(allCookies).filter(([k]) => cookieAllowlist.includes(k)))
        : allCookies,
    );
    const redditIds = readRedditIds(getCookie);

    const payload = {
      event_name: eventName,
      event_id:
        options.event_id ||
        (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
        `${Date.now()}_${Math.random()}`,
      event_time: Math.floor(Date.now() / 1000),

      // your app-level identity / join key
      visitor_id: visitorId,

      // flexible event data (purchase, lead, etc.)
      ...eventData,

      // universal context
      context: {
        user_agent: navigator.userAgent,
        page_location: window.location.href,
        page_referrer: document.referrer || null,
        language: navigator.language || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      },

      // identifiers = cookies + stored click ids in 1p cookies
      identifiers: {
        // keep your existing ones for convenience
        ga_client_id: getCookie('_ga') ? getCookie('_ga')!.substring(6) : null,
        // GA4 session stitching — without these the server-sent events land in
        // a session-less context (Landing page / source = "(not set)").
        ga_session_id: getGaSession().id,
        ga_session_number: getGaSession().number,
        fbp: getCookie('_fbp') || null,
        fbc: getCookie('_fbc') || null,
        // Reddit click id + pixel cookie (server lifts both to top-level rdt_cid / rdt_uuid)
        rdt_cid: redditIds.rdtCid,
        rdt_uuid: redditIds.rdtUuid,

        // all cookies or allowlisted cookies
        cookies,

        // click ids persisted as cookies
        click_ids: {
          ...Object.fromEntries(Object.entries(allCookies).filter(([k]) => k.startsWith('cid_'))),
          // Binom postback id (sGTM {{bclickId}}): the real tracker click id
          // first — user.bclickId can hold an fbclid/gclid for newer signups.
          b_click_id:
            authStore.user?.thriveTrackClickId || getCookie(BINOM_CLICK_ID_COOKIE) || authStore.user?.bclickId || null,
        },

        utm: Object.fromEntries(Object.entries(allCookies).filter(([k]) => k.startsWith('utm_'))),
      },
    };

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };

      // Add sGTM preview header if preview token exists
      const previewToken = getSgtmPreviewToken();
      if (previewToken) {
        headers['x-gtm-server-preview'] = previewToken;
      }

      await $fetch('/api/tracking/event', {
        method: 'POST',
        headers,
        body: payload,
        keepalive: true,
      });
    } catch (error) {
      console.error('Error sending tracking event:', error);
    }
  };

  return {
    getCookie,
    setCookie,
    getAllCookies,
    persistClickIds,
    recordRegistrationSource,
    registrationSourceParams,
    ensureVisitorId,
    sendEventToBackend,
  };
};
